<template>
    <div class="shop-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && shop.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="shop"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>

                <div v-if="editMode" class="card mt-3">
                    <div class="card-body">
                        <h6
                            class="col-12 mb-3 text-uppercase text-center bg-light p-2"
                        >
                            Active tags
                        </h6>
                        <filterable-tags :shop-id="id" />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <router-link
                                v-if="editMode"
                                :to="`/shops/${id}/products`"
                                class="btn btn-secondary btn-static waves-effect waves-light m-1"
                            >
                                <i class="fab fa-product-hunt" />
                                Products
                            </router-link>

                            <a
                                v-if="editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>

                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="shop"
                            :slug="shop.slug"
                            :options="{ slugBasicUrl: 'shops' }"
                            :slug-error="slugError"
                            :featured-image="shop.featuredImageId"
                            :campuses="shop.campuses"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                            @on-featured-image-change="onFeaturedImageChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import { frontendUrl } from '@/config';
import { PaymentMethod } from '@/enums/paymentMethods';
import AddEditForm from '@/components/shops/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import FilterableTags from '@/components/product-tags/FilterableView';

export default {
    page: {
        title: 'Manage Shop'
    },

    validations: {
        shop: {
            name: {
                required
            },
            slug: {
                required
            },
            terminal: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        FilterableTags,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit shop' : 'Add shop',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Shops',
                    to: '/shops'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            shop: {
                meta: {},
                campuses: [],
                terminal: PaymentMethod.IFRAME
            },
            informationKey: 0,
            serverErrors: [],
            shopValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true,
                featuredImage: true,
                campuses: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/shops/${this.shop.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.shop,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.shop = await this.getShop(this.id);
                this.shopValueStringify = JSON.stringify(this.shop);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addShop: 'shops/store',
            deleteShop: 'shops/destroy',
            getShop: 'shops/show',
            updateShop: 'shops/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.shop.campuses.map(campus => campus.id);

            const data = {
                ...this.shop,
                campuses,
                payWithStars: !!this.shop.payWithStars
            };

            try {
                if (!this.editMode) {
                    const added = await this.addShop(data);

                    this.$router.push(`/shops/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateShop({
                        id: this.id,
                        data
                    });

                    this.shop.updatedAt = updatedAt;
                    this.shopValueStringify = JSON.stringify(this.shop);

                    this.$toaster('Shop updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteShop(this.id);

                this.$router.push('/shops');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.shopValueStringify !== JSON.stringify(this.shop)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.shop.meta = { ...meta };
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.shop.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.shop.slug = slug;
        },

        onFeaturedImageChange(imageId) {
            this.shop.featuredImageId = imageId;
        },

        onCampusesChange(campuses) {
            this.shop.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
